import { useMutation } from '@apollo/client/react'
import { Formik } from 'formik'
import React from 'react'

import { Button } from '../../../../../components/Button'
import { Currency } from '../../../../../components/Currency'
import { DisplayDate } from '../../../../../components/DisplayDate'
import { ButtonColumn, ButtonRow, Heading1, Row, Text } from '../../../../../components/layout'
import { CheckboxInput } from '../../../../../components/layout/form/input'
import { ScrollToError } from '../../../../../components/layout/form/ScrollToError'
import { FormikForm as Form } from '../../../../../components/layout/form/style'
import { Loading } from '../../../../../components/responses'
import { setErrorNotifications } from '../../../../../components/responses/setErrorNotifications'
import { TopNavigation } from '../../../../../components/TopNavigation/TopNavigation'
import { CONFIRM_BILLING } from './confirmMutation'
import { messages as billingConfirmationMessages } from './messages'
import { toConfirmBillingMutationVariables } from './toConfirmBillingMutationVariables'
import { Billing, ConfirmBillingFormValues } from './types'
import { formatMessageSafe } from '../../../../../util/translateInputOptions'
import { useIntl } from 'react-intl'

interface ModalProps {
  billing: Billing
  towingRecordId: number
  onConfirmBilling: () => void
  close: () => void
  continueEditing: () => void
}

const BillingConfirmationIntl: React.FunctionComponent<ModalProps> = ({
  billing,
  onConfirmBilling,
  towingRecordId,
  close,
  continueEditing,
}) => {
  const intl = useIntl()
  const {
    type,
    paymentDetails: { vattedAmount, debtorName, receiptEmail, paymentDate },
  } = billing
  const formatMessage = formatMessageSafe(intl)
  const [confirmBilling, { loading }] = useMutation(CONFIRM_BILLING, {
    onCompleted(result: any) {
      if (result.confirmBilling.__typename === 'ConfirmBillingSuccess') {
        onConfirmBilling()
      } else {
        setErrorNotifications({ data: result.confirmBilling })
      }
    },
  })

  return (
    <Formik
      initialValues={{ towingRecordId, sendReceipt: false }}
      onSubmit={(values: ConfirmBillingFormValues) => {
        confirmBilling({
          variables: toConfirmBillingMutationVariables(values),
        })
      }}
    >
      {() => {
        return (
          <Form onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && e.preventDefault()}>
            <TopNavigation />
            <Row justify="center">
              <Heading1>Kuittaa maksu</Heading1>
            </Row>
            <Row justify="center">
              <Text fontWeight="700" size="big">
                {formatMessage(billingConfirmationMessages[type])} <Currency>{vattedAmount ?? 0}</Currency>
              </Text>
            </Row>
            <Row justify="center">
              <Text size="big">{debtorName}</Text>
            </Row>
            <Row justify="center">
              <Text size="big">
                <DisplayDate date={paymentDate} />
              </Text>
            </Row>
            {receiptEmail != null && receiptEmail !== '' && (
              <Row justify="center">
                <CheckboxInput name="sendReceipt" label={`Lähetä kuitti ${receiptEmail}`} />
              </Row>
            )}

            <Row justify="center">
              <ButtonRow>
                <ButtonColumn>
                  <Button category="save" label="Merkitse maksetuksi" disabled={loading} submit />
                </ButtonColumn>
                <ButtonColumn>
                  <Button category="new" label="Jätä avoimeksi" disabled={loading} onClick={close} />
                </ButtonColumn>
                <ButtonColumn>
                  <Button category="new" label="Jatka muokkausta" disabled={loading} onClick={continueEditing} />
                </ButtonColumn>
              </ButtonRow>
            </Row>
            <ScrollToError />
            <Loading loading={loading} />
          </Form>
        )
      }}
    </Formik>
  )
}

export const BillingConfirmation = BillingConfirmationIntl
